import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Loader2, UploadIcon } from 'lucide-react';

/**
 * A component that provides a drag-and-drop area for file uploads,
 * handling file validation and providing feedback to the user.
 *
 * @param {object} props - The component props.
 * @param {string} props.uploadStatus - Indicates the current status of the upload (e.g., 'idle', 'uploading', 'success', 'error'). Allows parent to control the display state.
 * @param {string|null} props.errorMsg - An error message provided by the parent component to display within the drag-and-drop zone if an upload fails or another error occurs.
 * @param {function(File): void} props.onFileReadyToUpload - Callback function triggered when a valid file is selected or dropped, providing the File object to the parent for handling the upload.
 * @param {object} [props.options] - Configuration options for the file upload behaviour and display.
 * @param {string[]} [props.options.accepts=['.pdf', '.docx', '.txt']] - Array of accepted file extensions (e.g., '.pdf', '.docx').
 * @param {number} [props.options.maxFileSizeMB=10] - Maximum allowed file size in megabytes.
 * @param {number} [props.options.acceptsDocCount=1] - Maximum number of documents allowed to be processed (intended for future use or specific validation logic, defaults to 1).
 * @param {string|null} [props.options.acceptsOverrideText=null] - Optional text to override the default display message listing accepted file types.
 */
const DragAndDropFileUpload = ({
    uploadStatus,
    errorMsg,
    onFileReadyToUpload,
    options = {
        accepts: ['.pdf', '.docx', '.txt'],
        maxFileSizeMB: 10,
        acceptsDocCount: 1,
        acceptsOverrideText: null
    }
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    // Calculate display text based on NEW options structure
    const descriptionText = useMemo(() => {
        // If override text is provided, use it directly
        if (options?.acceptsOverrideText) {
            return options.acceptsOverrideText;
        }

        // Otherwise, calculate from the accepts array
        if (!options?.accepts || options.accepts.length === 0) {
            return 'No file types specified.'; // Fallback
        }

        // Process the accepts array: remove dot, uppercase, get unique
        const types = [
            ...new Set(
                options.accepts.map(ext =>
                    ext.startsWith('.') ? ext.substring(1).toUpperCase() : ext.toUpperCase()
                )
            )
        ].join(', ');

        return `Accepts ${types}, up to ${options.maxFileSizeMB}MB`;

    }, [options]); // Recalculate only if options change

    // Generate the accept string for the file input
    const acceptString = useMemo(() => {
        return options?.accepts?.join(',') || ''; // e.g., ".pdf,.docx"
    }, [options]);

    // --- Internal Drag and Drop Handlers ---
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (uploadStatus !== 'uploading') {
            setIsDragging(true);
        }
    }, [uploadStatus]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Prevent flickering when dragging over child elements
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation(); // Necessary to allow drop
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (uploadStatus !== 'uploading' && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const droppedFile = e.dataTransfer.files[0];
            onFileReadyToUpload(droppedFile); // Call the parent callback
            e.dataTransfer.clearData();
        }
    }, [uploadStatus, onFileReadyToUpload]);

    // --- Internal Input Change Handler ---
    const handleFileChange = useCallback((e) => {
        if (uploadStatus !== 'uploading' && e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            onFileReadyToUpload(selectedFile); // Call the parent callback
            // Reset the input value so the same file can be selected again if removed
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [uploadStatus, onFileReadyToUpload]);

    // --- Internal Click Handler for the Drop Zone ---
    const handleZoneClick = useCallback(() => {
        if (uploadStatus !== 'uploading' && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [uploadStatus]);


    return (
        <div
            className={`relative w-full flex flex-col items-center justify-center px-6 py-16 border-2 min-h-[180px] ${isDragging ? 'border-primary bg-accent/80' : 'border-border'} border-dashed rounded-md transition-colors ${uploadStatus !== 'uploading' ? 'cursor-pointer' : 'cursor-default'}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleZoneClick}
        >
            {/* Loading Indicator */}
            <If condition={uploadStatus === 'uploading'}>
                <div className="absolute inset-0 bg-accent flex flex-col items-center justify-center z-10 rounded-md">

                    <Loader2 className="animate-spin" />
                    <p className="mt-2 text-sm font-medium">Uploading...</p>
                </div>
            </If>

            {/* Default Upload UI */}
            <div className={`space-y-2 text-center ${uploadStatus === 'uploading' ? 'invisible' : ''}`}>
                <div className="inline-flex items-center justify-center p-2 rounded-md bg-accent mb-4">
                    <UploadIcon
                        className="h-5 w-5 text-primary stroke-2"
                        aria-hidden="true"
                    />
                </div>
                <div className="flex text-base justify-center mb-0">
                    <span
                        className="relative rounded-md font-normal text-md text-gray-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-gray-500"
                    >
                        Click or drag and drop to upload
                    </span>
                    {/* Use internal ref and handler */}
                    <input
                        ref={fileInputRef}
                        id="file-upload"
                        accept={acceptString}
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        disabled={uploadStatus === 'uploading'}
                        multiple={options.acceptsDocCount > 1}
                    />
                </div>
                {/* Display the calculated or override text */}
                <p className="text-md text-gray-400 font-light">{descriptionText}</p>
                {errorMsg && (
                    <p className="text-sm text-red-600 font-medium pt-1">{errorMsg}</p>
                )}
            </div>
        </div>
    );
};

export default DragAndDropFileUpload;